import { useRef, useState } from "react";
import TurnstileWidget from "plg/screens/Signup/TurnstileWidget";
import type { TurnstileHandle } from "plg/screens/Signup/TurnstileWidget";
import { submitHumanCheck } from "plg/api/plgAuth";
import { saveHumanPass } from "plg/auth/humanPass";
import type { SignInError } from "plg/screens/Signup/authStartResult";
import "./MagicLink.scss";

type GateError = "failed" | "tooMany" | "unavailable" | "generic";

const MESSAGES: Record<GateError, string> = {
  failed: "We could not confirm you are human. Please try again.",
  tooMany: "Too many attempts. Try again later.",
  unavailable: "The human check could not load. Refresh the page to try again.",
  generic: "Something went wrong. Please try again.",
};

interface Props {
  siteKey: string | undefined;
  onPassed: () => void;
  /** Errors that replace the whole card (COUNTRY_NOT_SUPPORTED). */
  onBlocked: (error: SignInError) => void;
}

/**
 * v2.2 (D24): the first thing on /signup. One managed Turnstile widget; its token is exchanged
 * for a short-lived human pass, and only then are Google, Microsoft and the email field shown.
 */
function HumanGate({ siteKey, onPassed, onBlocked }: Props) {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<GateError | null>(null);
  const widget = useRef<TurnstileHandle>(null);

  const onToken = async (token: string | null) => {
    if (!token || checking) return;
    setChecking(true);
    setError(null);
    const res = await submitHumanCheck({ turnstileToken: token });
    setChecking(false);
    if (res.ok === true) {
      saveHumanPass(res.data.humanPass, res.data.expiresInSeconds);
      onPassed();
      return;
    }
    widget.current?.reset(); // a Turnstile token is single use
    if (res.code === "COUNTRY_NOT_SUPPORTED") onBlocked("country");
    else if (res.code === "CAPTCHA_FAILED") setError("failed");
    else if (res.code === "TOO_MANY_ATTEMPTS" || res.status === 429) setError("tooMany");
    else setError("generic");
  };

  return (
    <div className="plg-gate">
      {/* No heading of our own: the Turnstile widget already says "Verify you are human" (product 2026-10-28). */}
      {siteKey ? (
        <TurnstileWidget
          ref={widget}
          siteKey={siteKey}
          visible
          onToken={onToken}
          onLoadError={() => setError("unavailable")}
        />
      ) : (
        !error && <div className="plg-gate__loading">Loading…</div>
      )}
      {checking && (
        <div className="plg-gate__loading" role="status">
          Checking…
        </div>
      )}
      {error && (
        <div className="plg-signup__note" role="alert">
          {MESSAGES[error]}
        </div>
      )}
    </div>
  );
}

export default HumanGate;
